import React from 'react';
import { NavLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import Divider from '@mui/material/Divider';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Tooltip from '@mui/material/Tooltip';
import type { ProductionRoute } from './productionRoutes';

interface Props {
  routes: ProductionRoute[];
  collapsed: boolean;
}

/**
 * Map a route path to a stable data-testid slug using its last segment, e.g.
 * `/production/board` → `nav-board`.
 */
const slugFromPath = (p: string): string => {
  const segs = p.split('/').filter(Boolean);
  const last = segs.pop();
  return last === 'production' || last === undefined ? 'board' : last;
};

/**
 * SidebarNav — renders one `<NavLink>` per production route with the Abaris
 * orange (`#CB6015`) accent applied when active. The forme-grouped Gantt board
 * is the planning destination, followed by the ink workflow (Catalogue → Assign
 * → Sticker → Demand). Deprioritised views (`pinBottom`) sink below a flex
 * spacer so the primary block stays at eye level. The old TodayView "Schedule"
 * landing was retired 2026-06-23.
 */
export default function SidebarNav({ routes, collapsed }: Props) {
  const renderItem = (r: ProductionRoute) => {
    // navPath: link to a concrete URL when the route itself is a pattern (a
    // tabbed shell is routed at /ink/:tab but must link to /ink/stock).
    // matchPrefix: stay lit across a shell's child tabs instead of only on an
    // exact match, which `end` would demand.
    const to = r.navPath ?? r.path;
    const slug = slugFromPath(to);
    const testId = `nav-${slug}`;
    const inner = (
      <NavLink
        key={r.path}
        to={to}
        end={!r.matchPrefix}
        style={({ isActive }) => ({
          display: 'block',
          textDecoration: 'none',
          backgroundColor: isActive ? '#CB6015' : 'transparent',
          color: isActive ? '#FFFFFF' : 'inherit',
        })}
      >
        <ListItemButton
          sx={{ px: 3, py: 1.5, minHeight: 48 }}
          data-testid={testId}
        >
          <ListItemIcon sx={{ minWidth: collapsed ? 0 : 40, color: 'inherit' }}>
            {r.icon}
          </ListItemIcon>
          {!collapsed && (
            <ListItemText
              primary={r.label}
              primaryTypographyProps={{ fontWeight: 500 }}
            />
          )}
        </ListItemButton>
      </NavLink>
    );
    const node = collapsed ? (
      <Tooltip key={r.path} title={r.label} placement="right">
        <span>{inner}</span>
      </Tooltip>
    ) : (
      inner
    );
    return (
      <React.Fragment key={r.path}>
        {r.dividerBefore && <Divider sx={{ my: 1 }} />}
        {node}
      </React.Fragment>
    );
  };

  // `hidden` routes are still routed (legacy-path redirects) — they just don't
  // belong in the nav.
  const shown = routes.filter((r) => !r.hidden);
  const primary = shown.filter((r) => !r.pinBottom);
  const parked = shown.filter((r) => r.pinBottom);

  return (
    <List sx={{ py: 1, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
      {primary.map(renderItem)}
      {/* Spacer pushes the parked (not-actively-pursued) views to the bottom. */}
      <Box sx={{ flexGrow: 1, minHeight: 8 }} />
      {parked.length > 0 && <Divider sx={{ my: 1 }} />}
      {parked.map(renderItem)}
    </List>
  );
}
